import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import { useApiClient } from '../../auth/hooks/use-api-client.hook';
import { DocumentType, ResourceType } from '../core/types';
import { useResourceEndpoints } from '../core/use-resource-endpoints.hook';
import { addResponseFields, handleResourceError, shouldUsePostForUrl } from '../utils/resource-hooks.utils';

export interface ResourceCallVariables {
	/** The resource ID (empty string for collection-level calls) */
	resourceId: string;
	/** The method name */
	method: string;
	/** Method-specific parameters */
	params?: Record<string, unknown>;
	/** HTTP method to use */
	httpMethod?: 'GET' | 'POST';
}

export interface ResourceCallConfig {
	/** The resource type */
	resourceType: ResourceType | DocumentType;
	/** Fields to return in response */
	responseFields?: string[];
	/** Whether to use /call/ prefix (default: true) */
	useCallPrefix?: boolean;
	/** Request timeout in milliseconds (default: 60000) */
	timeout?: number;
}

/**
 * Generic hook for calling a method on a resource
 *
 * @example
 * ```tsx
 * // Call a method on a specific resource
 * const { mutateAsync: callMethod } = useResourceCallMutation({
 *   resourceType: ResourceType.TICKETS,
 *   responseFields: ['_id', 'status']
 * });
 *
 * await callMethod({
 *   resourceId: '123',
 *   method: 'setLabel',
 *   params: { labelId: '456' },
 *   httpMethod: 'POST'
 * });
 *
 * // Call a collection-level method
 * await callMethod({
 *   resourceId: '',
 *   method: 'count',
 *   params: { ticketTypeId: '789' },
 *   httpMethod: 'POST'
 * });
 * ```
 */
export const useResourceCallMutation = <TResponse = unknown, TError = unknown, TContext = unknown>(
	config: ResourceCallConfig,
	options?: UseMutationOptions<TResponse, TError, ResourceCallVariables, TContext>
) => {
	const apiClient = useApiClient();
	const endpoints = useResourceEndpoints(config.resourceType);

	return useMutation<TResponse, TError, ResourceCallVariables, TContext>(
		async ({ resourceId, method, params, httpMethod = 'POST' }) => {
			if (!method || typeof method !== 'string') {
				throw new Error('Method name is required and must be a string');
			}

			try {
				const body: Record<string, unknown> = params ? { ...params } : {};

				const bodyWithFields = addResponseFields(body, config.responseFields);

				const useCallPrefix = config.useCallPrefix !== false;
				const url = endpoints.getCallUrl(resourceId, method, useCallPrefix);

				let finalHttpMethod = httpMethod;
				if (httpMethod === 'GET' && shouldUsePostForUrl(url, bodyWithFields)) {
					finalHttpMethod = 'POST';
				}

				if (finalHttpMethod === 'POST') {
					// Always a JSON object: `call/*` endpoints answer 400 "Invalid JSON document" to an empty body.
					return await apiClient.post<TResponse>(url, bodyWithFields);
				}
				return await apiClient.get<TResponse>(
					url,
					Object.keys(bodyWithFields).length > 0 ? bodyWithFields : undefined
				);
			} catch (error) {
				throw handleResourceError(error);
			}
		},
		options
	);
};
